import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Breadcrumb from "@/components/ui/breadcrumb";
import Textarea from "@/components/ui/textarea";
import Input from "@/components/ui/input";
import Dropdown from "@/components/ui/dropdown";
import Button from "@/components/ui/button";
import Switch from "@/components/ui/switch";
import FileInput from "@/components/ui/file-input";
import { ALLOWED_FILE_TYPE_PRESETS } from "@/types/file-upload.types";
import { Check, Close, Delete, Plus } from "@/components/icons";
import {
  examManagementApi,
  type QuestionDifficulty,
} from "../services/exam-management.api";
import styles from "./CreateQuestionPoolPage.module.scss";

type QuestionOption = { id: string; text: string; isCorrect: boolean };
type DraftQuestion = {
  id: string;
  text: string;
  difficulty: QuestionDifficulty;
  allowMultipleSelection: boolean;
  imageFileID?: string;
  imagePreview?: string;
  options: QuestionOption[];
};

const difficultyOptions = [
  { label: "Kolay", value: "EASY" as QuestionDifficulty },
  { label: "Orta", value: "MEDIUM" as QuestionDifficulty },
  { label: "Zor", value: "HARD" as QuestionDifficulty },
];

const createEmptyOption = (): QuestionOption => ({
  id: crypto.randomUUID(),
  text: "",
  isCorrect: false,
});

const createEmptyQuestion = (): DraftQuestion => ({
  id: crypto.randomUUID(),
  text: "",
  difficulty: "MEDIUM",
  allowMultipleSelection: false,
  options: [createEmptyOption(), createEmptyOption(), createEmptyOption(), createEmptyOption()],
});

const CreateQuestionPoolPage: React.FC = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<DraftQuestion[]>([createEmptyQuestion()]);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
  const [saving, setSaving] = useState(false);

  const selectedQuestion = questions[selectedQuestionIndex];

  const breadcrumbItems = [
    { label: "Sorular", onClick: () => navigate("/dashboard/exam-management/question-pool") },
    { label: "Yeni Soru Oluştur" },
  ];

  const questionSummary = useMemo(
    () =>
      questions.map((q, index) => ({
        index,
        title: q.text.trim() || `${index + 1}. soru`,
        optionCount: q.options.filter((o) => o.text.trim()).length,
        hasCorrect: q.options.some((o) => o.isCorrect),
      })),
    [questions],
  );

  const updateSelectedQuestion = (patch: Partial<DraftQuestion>) => {
    setQuestions((prev) => prev.map((q, i) => (i === selectedQuestionIndex ? { ...q, ...patch } : q)));
  };

  const handleOptionChange = (optionId: string, text: string) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === selectedQuestionIndex
          ? { ...q, options: q.options.map((opt) => (opt.id === optionId ? { ...opt, text } : opt)) }
          : q,
      ),
    );
  };

  const handleSetCorrectOption = (optionId: string) => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== selectedQuestionIndex) return q;
        return {
          ...q,
          options: q.options.map((opt) => ({
            ...opt,
            isCorrect: q.allowMultipleSelection ? (opt.id === optionId ? !opt.isCorrect : opt.isCorrect) : opt.id === optionId,
          })),
        };
      }),
    );
  };

  const handleAddOption = () => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === selectedQuestionIndex ? { ...q, options: [...q.options, createEmptyOption()] } : q)),
    );
  };

  const handleRemoveOption = (optionId: string) => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== selectedQuestionIndex) return q;
        const filtered = q.options.filter((o) => o.id !== optionId);
        if (filtered.length < 2) return q;
        return { ...q, options: filtered };
      }),
    );
  };

  const handleDeleteQuestion = (index: number) => {
    if (questions.length <= 1) return;
    setQuestions((prev) => prev.filter((_, i) => i !== index));
    setSelectedQuestionIndex((prev) => Math.min(prev, Math.max(0, index - 1)));
  };

  const handleAddQuestion = () => {
    setQuestions((prev) => [...prev, createEmptyQuestion()]);
    setSelectedQuestionIndex(questions.length);
  };

  const validateQuestions = () => {
    const nonEmptyQuestions = questions.filter((q) => q.text.trim());
    if (nonEmptyQuestions.length === 0) {
      toast.error("En az bir soru girin.");
      return false;
    }
    for (let i = 0; i < nonEmptyQuestions.length; i++) {
      const q = nonEmptyQuestions[i];
      const optionsWithText = q.options.filter((o) => o.text.trim());
      if (optionsWithText.length < 2) {
        toast.error(`${i + 1}. soruda en az 2 seçenek olmalı.`);
        return false;
      }
      if (!optionsWithText.some((o) => o.isCorrect)) {
        toast.error(`${i + 1}. soruda en az 1 doğru seçenek seçin.`);
        return false;
      }
    }
    return true;
  };

  const handleCreate = async () => {
    if (!validateQuestions()) return;
    setSaving(true);
    try {
      const payload = questions
        .filter((q) => q.text.trim())
        .map((q) => ({
          questionText: q.text.trim(),
          difficulty: q.difficulty,
          allowMultipleSelection: q.allowMultipleSelection,
          imageFileID: q.imageFileID,
          options: q.options
            .filter((o) => o.text.trim())
            .map((o) => ({ optionText: o.text.trim(), isCorrect: o.isCorrect })),
        }));
      const count = await examManagementApi.bulkCreateQuestionPool(payload);
      toast.success(`${count || payload.length} soru oluşturuldu`);
      navigate("/dashboard/exam-management/question-pool");
    } catch {
      toast.error("Sorular oluşturulamadı");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <Header title="Soru Havuzu" />
      <div className={styles.container}>
        <div className={styles.headerRow}>
          <div className={styles.titleWrap}>
            <h1>Yeni Sorular Oluştur</h1>
            <p>Tek seferde birden fazla soru ekleyin ve kaydedin.</p>
          </div>
          <Button variant="contained" color="primary" onClick={() => void handleCreate()} loading={saving}>
            Soruları Oluştur
          </Button>
        </div>

        <Breadcrumb items={breadcrumbItems} />

        <div className={styles.questionsStep}>
          <div className={styles.questionsColumns}>
            <div className={styles.questionsListColumn}>
              <div className={styles.questionsListCard}>
                {questions.map((q, index) => (
                  <div
                    key={q.id}
                    role="button"
                    tabIndex={0}
                    className={`${styles.questionListItem} ${index === selectedQuestionIndex ? styles.questionListItemActive : ""}`}
                    onClick={() => setSelectedQuestionIndex(index)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedQuestionIndex(index);
                      }
                    }}
                  >
                    <div>
                      <span className={styles.questionListLabel}>{index + 1}. Soru</span>
                      {q.text ? <p className={styles.questionListText}>{q.text}</p> : null}
                    </div>
                  </div>
                ))}
              </div>

              <div className={styles.questionsSummaryCard}>
                <div className={styles.questionsSummaryHeader}>
                  <strong>Eklenen Sorular</strong>
                  <span className={styles.questionsSummaryCount}>{questions.length}</span>
                </div>
                <div className={styles.questionsSummaryList}>
                  {questionSummary.map((item) => (
                    <button
                      key={questions[item.index]?.id}
                      type="button"
                      className={`${styles.questionsSummaryItem} ${item.index === selectedQuestionIndex ? styles.questionsSummaryItemActive : ""}`}
                      onClick={() => setSelectedQuestionIndex(item.index)}
                    >
                      <span>{item.index + 1}. {item.title}</span>
                      <span>{item.optionCount} şık{item.hasCorrect ? " · doğru var" : " · doğru yok"}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.questionEditorColumn}>
              {selectedQuestion && (
                <div className={styles.questionEditorCard}>
                  <div className={styles.questionEditorHeader}>
                    <h3>{selectedQuestionIndex + 1}. Soru</h3>
                    <button
                      type="button"
                      className={styles.questionDeleteBtn}
                      onClick={() => handleDeleteQuestion(selectedQuestionIndex)}
                      disabled={questions.length <= 1}
                    >
                      <Delete width={20} height={20} />
                    </button>
                  </div>
                  <div className={styles.questionEditorBody}>
                    <Textarea
                      label="Soru"
                      placeholder="Soru metnini girin"
                      value={selectedQuestion.text}
                      onChange={(e) => updateSelectedQuestion({ text: e.target.value })}
                    />

                    <div className={styles.rowTwo}>
                      <Dropdown
                        label="Zorluk"
                        options={difficultyOptions}
                        value={selectedQuestion.difficulty}
                        selectedLabel={difficultyOptions.find((d) => d.value === selectedQuestion.difficulty)?.label ?? "Orta"}
                        onSelect={(value) => {
                          const selected = difficultyOptions.find((d) => d.value === value || d.label === value);
                          if (selected) updateSelectedQuestion({ difficulty: selected.value });
                        }}
                      />
                      <label className={styles.checkboxRow}>
                        <Switch
                          checked={selectedQuestion.allowMultipleSelection}
                          onChange={(checked) => updateSelectedQuestion({ allowMultipleSelection: checked })}
                        />
                        <span>Çoklu seçim</span>
                      </label>
                    </div>

                    <FileInput
                      key={selectedQuestionIndex}
                      label="Soru görseli"
                      placeholder="Soru için görsel yükleyin"
                      accept={ALLOWED_FILE_TYPE_PRESETS.IMAGE.map((f) => `.${f.toLowerCase()}`).join(",")}
                      supportedFormats={ALLOWED_FILE_TYPE_PRESETS.IMAGE}
                      maxSize={10}
                      maxFiles={1}
                      fileType="image"
                      uploadToBackend
                      onUploadSuccess={(fileId, previewUrl) =>
                        updateSelectedQuestion({ imageFileID: fileId, imagePreview: previewUrl })
                      }
                      onFileRemove={() => updateSelectedQuestion({ imageFileID: undefined, imagePreview: undefined })}
                      existingFileUrl={selectedQuestion.imagePreview}
                      enableDemoProgress={false}
                    />

                    <div className={styles.answerOptions}>
                      {selectedQuestion.options.map((opt) => (
                        <div
                          key={opt.id}
                          className={`${styles.answerOptionRow} ${opt.isCorrect ? styles.answerOptionRowSelected : ""}`}
                        >
                          <button type="button" className={styles.answerRadio} onClick={() => handleSetCorrectOption(opt.id)}>
                            {opt.isCorrect && <Check width={14} height={14} color="#15AC77" />}
                          </button>
                          <div className={styles.answerOptionInputWrap}>
                            <Input
                              placeholder="Seçenek metni"
                              value={opt.text}
                              onChange={(e) => handleOptionChange(opt.id, e.target.value)}
                            />
                          </div>
                          <button
                            type="button"
                            className={styles.removeOptionBtn}
                            onClick={() => handleRemoveOption(opt.id)}
                            disabled={selectedQuestion.options.length <= 2}
                          >
                            <Close width={18} height={18} color="#707a8f" border={false} />
                          </button>
                        </div>
                      ))}
                    </div>

                    <button type="button" className={styles.addOptionLink} onClick={handleAddOption}>
                      + Seçenek Ekle
                    </button>

                    <button type="button" className={styles.addQuestionBtn} onClick={handleAddQuestion}>
                      <Plus width={20} height={20} color="#707A8F" />
                      Yeni Soru Ekle
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CreateQuestionPoolPage;
